import { type JSX, useMemo } from "react";
import type { components } from "../../../api/schema";
import { BeatMarkerColumn } from "./BeatMarkerColumn";
import { TraceRow } from "./TraceRow";
import { UnexploredBranchIndicator } from "./UnexploredBranchIndicator";
import type { UIError } from "../../../errors/types";

type Scene = components["schemas"]["Scene"];
type WorldlineNode = components["schemas"]["WorldlineNode"];
type PlotDraft = components["schemas"]["PlotDraft"];

export type PalimpsestGridProps = {
  /** 描画対象の worldlines (並び順は問わない。内部で createdAt 昇順→降順で整列)。 */
  worldlines: WorldlineNode[];
  /** 各 worldline に属する scenes (古い順)。 */
  scenesByWorldline: Record<string, Scene[]>;
  /** 現在プレイ中の worldline id。TraceRow の classifyTrace → ongoing 判定に使う。 */
  currentWorldlineId: string | null;
  /** 骨格 beat マーカー表示用の PlotDraft。undefined / null / beats=null は marker 非表示。 */
  plotDraft?: PlotDraft | null;
  /** scene ノード tap 時のコールバック (上位 PalimpsestView が SceneDetailPanel を開く)。 */
  onTapScene: (scene: Scene, worldline: WorldlineNode) => void;
  /** 未踏分岐の AI 提案エラー時に親へ委譲する UIError (上位 toast へ)。 */
  onPreviewError?: (err: UIError) => void;
};

/**
 * 各 worldline の最終 scene で「まだ未踏の choice」を算出する。
 * - その worldline の子 worldline (= 既に分岐済み) の branchFromChoiceId を除外
 * - その choice が最終 scene のものであれば該当、それ以外 (中間 scene の既踏 choice)
 *   は現段階の MVP では考慮しない (将来拡張: 中間 scene の "?" 描画)
 */
function collectFinalSceneUntakenChoices(
  worldline: WorldlineNode,
  finalScene: Scene | undefined,
  children: WorldlineNode[],
): Array<{ sceneId: string; choiceId: string; choiceText: string }> {
  if (finalScene === undefined) return [];
  const branchedChoiceIds = new Set(
    children
      .filter((c) => c.branchFromSceneId === finalScene.id)
      .map((c) => c.branchFromChoiceId)
      .filter((id): id is string => id !== null && id !== undefined),
  );
  // final scene の choices のうち、
  //  - プレイヤーが選んだ choice (taken) は除外できないが、MVP では scene.choices 全てから
  //    既分岐分を除いた残りを「未踏」として扱う (taken 判定は history が必要)
  //  - 既分岐済 (別 worldline が存在) は除外
  const suggestions: Array<{ sceneId: string; choiceId: string; choiceText: string }> = [];
  for (const choice of finalScene.choices) {
    if (branchedChoiceIds.has(choice.id)) continue;
    suggestions.push({
      sceneId: finalScene.id,
      choiceId: choice.id,
      choiceText: choice.text,
    });
  }
  return suggestions;
}

/**
 * worldline-palimpsest (Phase 3-1, Task 5.6) 全筆跡の 2 次元配置統合コンポーネント。
 *
 * 縦: 筆跡順 (第一筆跡が下段、最新が上段)、横: scene index。
 * TraceRow × N を縦積みし、上層に BeatMarkerColumn を overlay 配置する。
 * 各 TraceRow の末尾には UnexploredBranchIndicator 群 (最終 scene の未踏 choice) を並べる。
 *
 * 筆跡 0 本時は空状態メッセージを表示 (Req 1.5)。PlotDraft 未 DI / beats null 時は
 * beat marker を非表示 (Req 3.4 fallback)。
 */
export function PalimpsestGrid({
  worldlines,
  scenesByWorldline,
  currentWorldlineId,
  plotDraft,
  onTapScene,
  onPreviewError,
}: PalimpsestGridProps): JSX.Element {
  // 時系列順 (createdAt asc) にソートして traceIndex を付与
  const sortedAsc = useMemo(() => {
    const copy = [...worldlines];
    copy.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return copy;
  }, [worldlines]);

  // 表示は「最新が上」= createdAt desc
  const displayOrder = useMemo(() => [...sortedAsc].reverse(), [sortedAsc]);

  const traceCount = sortedAsc.length;

  // 各 worldline の子 (分岐子) を引き当てる (UnexploredBranchIndicator の既分岐除外用)
  const childrenByParent = useMemo(() => {
    const m = new Map<string, WorldlineNode[]>();
    for (const wl of worldlines) {
      if (wl.parentId !== null && wl.parentId !== undefined) {
        const arr = m.get(wl.parentId) ?? [];
        arr.push(wl);
        m.set(wl.parentId, arr);
      }
    }
    return m;
  }, [worldlines]);

  if (worldlines.length === 0) {
    return (
      <div
        data-testid="palimpsest-grid-empty"
        className="flex items-center justify-center p-6 text-sm text-slate-500"
      >
        筆跡はまだありません。プレイを進めると世界線が刻まれていきます。
      </div>
    );
  }

  return (
    <div
      data-testid="palimpsest-grid"
      data-trace-count={traceCount}
      className="relative flex flex-col gap-1 px-2 py-3 rounded bg-slate-950/40"
    >
      {/* BeatMarkerColumn は absolute で grid 全体に overlay */}
      <BeatMarkerColumn plotDraft={plotDraft ?? null} />

      {displayOrder.map((wl) => {
        // traceIndex は sortedAsc (古い順) 基準。最新が上段表示でも opacity 計算は古い順 index を使う。
        const traceIndex = sortedAsc.findIndex((w) => w.id === wl.id);
        const scenes = scenesByWorldline[wl.id] ?? [];
        const finalScene = scenes[scenes.length - 1];
        const untakens = collectFinalSceneUntakenChoices(
          wl,
          finalScene,
          childrenByParent.get(wl.id) ?? [],
        );

        return (
          <div
            key={wl.id}
            className="flex items-center gap-2"
            data-testid={`palimpsest-grid-row-${wl.id}`}
          >
            <TraceRow
              worldline={wl}
              scenes={scenes}
              traceIndex={traceIndex}
              traceCount={traceCount}
              currentWorldlineId={currentWorldlineId}
              onTapScene={onTapScene}
            />
            {untakens.length > 0 && (
              <div
                className="flex items-center gap-1"
                data-testid={`palimpsest-grid-untakens-${wl.id}`}
              >
                {untakens.map((u) => (
                  <UnexploredBranchIndicator
                    key={`${wl.id}-${u.choiceId}`}
                    worldlineId={wl.id}
                    sceneId={u.sceneId}
                    choiceId={u.choiceId}
                    choiceText={u.choiceText}
                    {...(onPreviewError !== undefined ? { onPreviewError } : {})}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
